// @ts-check
/**
 * Product picker for the sale form. Tapping the field opens the full product list straight away, before
 * anything is typed; typing narrows it by name or SKU. Picking a product hands it back and closes the list.
 */
import { Package } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { filterProducts } from '../utils/posCatalog';
import { listPrice } from '../utils/posOrder';

// The list must outlive the input's blur long enough for the tap on an option to register.
const CLOSE_DELAY_MS = 150;

const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

/** @param {{products: any[], onPick: (product: any) => void}} props */
const PosProductPicker = ({ products, onPick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(/** @type {any} */ (null));
  const shown = filterProducts(products, query);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const pick = (product) => {
    onPick(product);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        className="w-full px-3 py-3 text-base border border-input rounded-lg"
        placeholder="Tap to choose a piece, or type to search..."
        value={query}
        onFocus={() => {
          clearTimeout(closeTimer.current);
          setOpen(true);
        }}
        onBlur={() => {
          closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
        }}
        onChange={(e) => setQuery(e.target.value)}
        role="combobox"
        aria-expanded={open}
        aria-controls="pos-product-list"
      />
      {open && (
        <div
          id="pos-product-list"
          role="listbox"
          className="absolute z-10 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto"
        >
          {shown.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">No matching products</p>
          ) : (
            shown.map((product) => (
              <button
                key={product.product_id}
                role="option"
                aria-selected="false"
                onClick={() => pick(product)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 border-b border-border/60 last:border-0"
              >
                {product.image ? (
                  <img src={product.image} alt="" loading="lazy" className="w-10 h-10 object-cover rounded-lg" />
                ) : (
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Package size={16} className="text-muted-foreground/50" />
                  </div>
                )}
                <span className="flex-1 text-sm">{product.name}</span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">{inr(listPrice(product))}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PosProductPicker;
